"use client";

import { AdminLayout } from "@/layouts/admin-layout";
import { getProductColumns, type ProductRow } from "./columns";
import { trpc } from "@/lib/trpc";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { RowSelectionState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel
} from "@tanstack/react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CreateProductDialog } from "@/components/admin/activation/CreateProductDialog";
import { EditProductDialog } from "@/components/admin/activation/EditProductDialog";
import { SortableProductRow } from "@/components/admin/activation/SortableProductRow"; // Will be created
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, GripVertical } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";

interface ProcessedCustomFeature {
  id: string;
  keyName: string;
  value: string | number | boolean;
  valueType: "text" | "number" | "boolean";
}

export default function AdminProductsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductRow | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductRow | null>(null);
  const [sortableProducts, setSortableProducts] = useState<ProductRow[]>([]);

  const { data, isLoading, error, refetch } = trpc.activation.listProducts.useQuery(
    {
      page,
      pageSize, // For drag and drop to work on all items, pagination might need to be disabled or handled differently.
                // For now, sorting will apply to the current page's items.
      search: debouncedSearchTerm || undefined,
      // Potentially add a parameter to fetch all products if sorting is enabled, e.g., disablePagination: true
    }
  );

  useEffect(() => {
    if (data?.items) {
      setSortableProducts(data.items as ProductRow[]);
    }
  }, [data?.items]);


  // For debugging if needed
  useMemo(() => {
    if (data?.items) {
      console.log("Raw Product Data:", JSON.stringify(data.items, null, 2));
      // 详细输出每个产品的customFeatures，帮助调试
      data.items.forEach((product, index) => {
        console.log(`Product ${index} (${product.name}) customFeatures:`,
          JSON.stringify(product.customFeatures, null, 2));

        if (product.customFeatures) {
          console.log(`Product ${index} customFeatures type:`, typeof product.customFeatures);
          console.log(`Product ${index} customFeatures isArray:`, Array.isArray(product.customFeatures));

          // 检查customFeatures在数据库中的格式
          if (typeof product.customFeatures === 'object' && !Array.isArray(product.customFeatures)) {
            console.log(`Product ${index} customFeatures as object:`, Object.keys(product.customFeatures));
          }

          // 如果是数组，检查数组元素格式
          if (Array.isArray(product.customFeatures) && product.customFeatures.length > 0) {
            console.log(`Product ${index} first feature:`, product.customFeatures[0]);
          }
        }
      });
    }
  }, [data?.items]);

  const utils = trpc.useUtils();
  const deleteProductMutation = trpc.activation.deleteProduct.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.products.deleteSuccess"));
      utils.activation.listProducts.invalidate();
      setIsDeleteDialogOpen(false);
      setProductToDelete(null);
    },
    onError: (err) => {
      toast.error(t("admin.activation.products.deleteError", { message: err.message }));
      setIsDeleteDialogOpen(false);
      setProductToDelete(null);
    },
  });

  const updateProductOrderMutation = trpc.activation.updateProductOrder.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.products.reorderSuccess"));
      utils.activation.listProducts.invalidate(); // Refetch to ensure consistency, though optimistic update is primary
    },
    onError: (err) => {
      toast.error(t("admin.activation.products.reorderError", { message: err.message }));
      // Optionally revert optimistic update here if needed, by refetching or resetting state
      refetch();
    },
  });

  const handleEdit = useCallback((product: ProductRow) => {
    setSelectedProduct(product);
    setIsEditSheetOpen(true);
  }, []);

  const handleDelete = useCallback((product: ProductRow) => {
    setProductToDelete(product);
    setIsDeleteDialogOpen(true);
  }, []);

  const confirmDelete = () => {
    if (productToDelete) {
      deleteProductMutation.mutate({ id: productToDelete.id });
    }
  };

  const columns = useMemo(
    () => getProductColumns({ onEdit: handleEdit, onDelete: handleDelete, tFunction: t }),
    [handleEdit, handleDelete, t]
  );

  const pageCount = data?.total ? Math.ceil(data.total / pageSize) : 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSortableProducts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrderedItems = arrayMove(items, oldIndex, newIndex);

        // Call mutation to update backend
        const orderedProductsPayload = newOrderedItems.map((item, index) => ({
          id: item.id,
          position: index, // Or whatever the backend expects for order/position
        }));
        updateProductOrderMutation.mutate({ orderedProducts: orderedProductsPayload });

        return newOrderedItems;
      });
    }
  };

  const table = useReactTable({
    data: sortableProducts, // Use sortableProducts for the table
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true, // Keep row selection if needed for other actions
    manualPagination: true, // Assuming pagination is still manual
    state: {
      rowSelection,
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onRowSelectionChange: setRowSelection,
    pageCount,
  });

  const selectedRowsData = table.getFilteredSelectedRowModel().rows.map((row) => row.original);

  if (error) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-10">
          <p>{t("common.errorPrefix")}: {error.message}</p>
        </div>
      </AdminLayout>
    );
  }

  const renderBulkActionsContent = (selectedData: ProductRow[]) => {
    // TODO: Implement bulk actions as needed
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm">{selectedData.length} {t("admin.activation.products.selected")}</span>
      </div>
    );
  };

  // Generate loading skeleton with proper keys
  const renderLoadingSkeleton = () => {
    // Generate stable unique IDs for skeleton rows
    return Array.from({ length: pageSize }).map((_, rowIndex) => {
      // Create a stable ID for this row based on page and rowIndex
      const stableRowId = `skeleton-${page}-${rowIndex}`;

      return (
        <TableRow key={stableRowId}>
          {/* Checkbox column */}
          <TableCell key={`${stableRowId}-checkbox`} className="py-3 w-16">
            <Skeleton className="h-4 w-4 rounded" />
          </TableCell>

          {/* Name column */}
          <TableCell key={`${stableRowId}-name`} className="py-3">
            <Skeleton className="h-5 w-44 rounded" />
          </TableCell>

          {/* Description column */}
          <TableCell key={`${stableRowId}-desc`} className="py-3">
            <div className="space-y-1">
              <Skeleton className="h-4 w-full max-w-[250px] rounded" />
              <Skeleton className="h-4 w-3/4 max-w-[180px] rounded" />
            </div>
          </TableCell>

          {/* Pricing column */}
          <TableCell key={`${stableRowId}-pricing`} className="py-3">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded" />
              </div>
            </div>
          </TableCell>

          {/* Features column */}
          <TableCell key={`${stableRowId}-features`} className="py-3">
            <div className="flex flex-wrap gap-1">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-6 w-14 rounded-full" />
            </div>
          </TableCell>

          {/* Actions column */}
          <TableCell key={`${stableRowId}-actions`} className="py-3">
            <div className="flex space-x-2">
              <Skeleton className="h-8 w-8 rounded" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t("admin.activation.products.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("admin.activation.products.description", "Manage activation products.")}</p>
          </div>
          <CreateProductDialog onProductCreated={refetch} />
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <Input
            placeholder={t("admin.users.page.searchByPlaceholder", {field: t("admin.activation.products.productName").toLowerCase()})}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full sm:w-72"
          />
        </div>

        <div className="space-y-6">
          {selectedRowsData.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm">{selectedRowsData.length} {t("admin.activation.products.selected")}</span>
            </div>
          )}

          <div className="bg-card rounded-md border shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="bg-muted/50">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className="font-medium py-4">
                        {header.isPlaceholder ? null : (
                          <div>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableProducts.map(p => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {isLoading && !sortableProducts.length ? ( // Show skeleton only if truly loading and no data yet
                      renderLoadingSkeleton()
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <SortableProductRow key={row.original.id} productRow={row} />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length + 1} className="h-24 text-center"> {/* +1 for drag handle column */}
                          {t("admin.activation.products.noResults")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </SortableContext>
              </DndContext>
            </Table>
          </div>

          <div className="flex items-center justify-between px-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} {t("common.of")} {table.getFilteredRowModel().rows.length} {t("common.rowsSelected")}
            </div>
            <div className="flex items-center space-x-6 lg:space-x-8">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">
                  {t("common.rowsPerPage")}
                </span>
                <Select
                  value={`${pageSize}`}
                  onValueChange={(value) => {
                    const newPageSize = Number(value);
                    setPageSize(newPageSize);
                  }}
                >
                  <SelectTrigger className="h-8 w-20">
                    <SelectValue placeholder={pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 20, 30, 40, 50].map((size) => (
                      <SelectItem key={size} value={`${size}`}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-[120px] items-center justify-center text-sm font-medium">
                {t("common.page")} {page} {t("common.of")} {pageCount || 1}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  <span className="sr-only">{t("common.firstPage")}</span>
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <span className="sr-only">{t("common.previousPage")}</span>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pageCount}
                >
                  <span className="sr-only">{t("common.nextPage")}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => setPage(pageCount)}
                  disabled={page === pageCount}
                >
                  <span className="sr-only">{t("common.lastPage")}</span>
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {selectedProduct && (
          <EditProductDialog
            product={{
              id: selectedProduct.id,
              name: selectedProduct.name,
              description: selectedProduct.description,
              monthlyPrice: selectedProduct.monthlyPrice != null ? Number(selectedProduct.monthlyPrice) : null,
              yearlyPrice: selectedProduct.yearlyPrice != null ? Number(selectedProduct.yearlyPrice) : null,
              customFeatures: (() => {
                let processedFeatures: ProcessedCustomFeature[] = []; // Define a variable to hold the result
                if (Array.isArray(selectedProduct.customFeatures)) {
                  // Ensure the items in the array conform to the expected type for EditProductDialog
                  processedFeatures = selectedProduct.customFeatures.map(feature => {
                    let determinedValueType: "text" | "number" | "boolean";
                    // Check if feature.valueType is already one of the allowed literals
                    if (feature.valueType === "text" || feature.valueType === "number" || feature.valueType === "boolean") {
                      determinedValueType = feature.valueType;
                    } else { // Otherwise, infer from the value itself
                      determinedValueType = typeof feature.value === 'boolean' ? 'boolean' :
                                            typeof feature.value === 'number' ? 'number' : 'text';
                    }
                    return {
                      id: feature.id || `feature-arr-${feature.keyName}-${Date.now()}-${Math.random().toString(36).substr(2,9)}`, // Ensure ID exists
                      keyName: feature.keyName,
                      value: feature.value,
                      valueType: determinedValueType
                    };
                  });
                } else if (typeof selectedProduct.customFeatures === 'object' && selectedProduct.customFeatures !== null) {
                  // Transform object-based customFeatures
                  processedFeatures = Object.entries(selectedProduct.customFeatures).map(([key, value]) => {
                    const valueType = typeof value === 'boolean' ? 'boolean' :
                                    typeof value === 'number' ? 'number' : 'text';
                    return {
                      id: `feature-obj-${key}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                      keyName: key,
                      value: value,
                      valueType: valueType
                    };
                  });
                }
                // If neither condition was met, processedFeatures remains [], which is the default
                return processedFeatures;
              })()
            }}
            isOpen={isEditSheetOpen}
            onOpenChange={setIsEditSheetOpen}
            onProductUpdated={() => {
              refetch();
              setSelectedProduct(null);
              setIsEditSheetOpen(false);
            }}
          />
        )}

        {productToDelete && (
          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("admin.activation.products.deleteConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("admin.activation.products.deleteConfirmDescription", { name: productToDelete.name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setProductToDelete(null)}>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={confirmDelete} disabled={deleteProductMutation.isPending}>
                  {deleteProductMutation.isPending ? t("common.deleting") : t("settings.apiKeys.actions.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </AdminLayout>
  );
}